import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type BadgeVariant = 'primary' | 'secondary' | 'destructive' | 'outline';
export type BadgeColor = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink';

export interface BadgeProps {
	children?: ReactNode;
	variant?: BadgeVariant;
	color?: BadgeColor;
	startIcon?: ReactNode;
	endIcon?: ReactNode;
	onRemove?: () => void;
	className?: string;
}

const primaryClasses: Record<BadgeColor, string> = {
	gray:   'bg-gray-500 text-white [--dot:theme(colors.white)]',
	red:    'bg-red-500 text-white [--dot:theme(colors.white)]',
	yellow: 'bg-yellow-500 text-white [--dot:theme(colors.white)]',
	green:  'bg-green-500 text-white [--dot:theme(colors.white)]',
	blue:   'bg-blue-500 text-white [--dot:theme(colors.white)]',
	indigo: 'bg-indigo-500 text-white [--dot:theme(colors.white)]',
	purple: 'bg-purple-500 text-white [--dot:theme(colors.white)]',
	pink:   'bg-pink-500 text-white [--dot:theme(colors.white)]',
};

const secondaryClasses: Record<BadgeColor, string> = {
	gray:   'bg-gray-100 text-gray-700 [--dot:theme(colors.gray.400)]',
	red:    'bg-red-100 text-red-700 [--dot:theme(colors.red.500)]',
	yellow: 'bg-yellow-100 text-yellow-700 [--dot:theme(colors.yellow.500)]',
	green:  'bg-green-100 text-green-700 [--dot:theme(colors.green.500)]',
	blue:   'bg-blue-100 text-blue-700 [--dot:theme(colors.blue.500)]',
	indigo: 'bg-indigo-100 text-indigo-700 [--dot:theme(colors.indigo.500)]',
	purple: 'bg-purple-100 text-purple-700 [--dot:theme(colors.purple.500)]',
	pink:   'bg-pink-100 text-pink-700 [--dot:theme(colors.pink.500)]',
};

const outlineClasses: Record<BadgeColor, string> = {
	gray:   'border border-gray-300 text-gray-600 bg-white [--dot:theme(colors.gray.400)]',
	red:    'border border-red-300 text-red-600 bg-white [--dot:theme(colors.red.500)]',
	yellow: 'border border-yellow-300 text-yellow-600 bg-white [--dot:theme(colors.yellow.500)]',
	green:  'border border-green-300 text-green-600 bg-white [--dot:theme(colors.green.500)]',
	blue:   'border border-blue-300 text-blue-600 bg-white [--dot:theme(colors.blue.500)]',
	indigo: 'border border-indigo-300 text-indigo-600 bg-white [--dot:theme(colors.indigo.500)]',
	purple: 'border border-purple-300 text-purple-600 bg-white [--dot:theme(colors.purple.500)]',
	pink:   'border border-pink-300 text-pink-600 bg-white [--dot:theme(colors.pink.500)]',
};

const destructiveClass = 'bg-red-600 text-white [--dot:theme(colors.white)]';

const variantDefaultColor: Record<BadgeVariant, BadgeColor> = {
	primary:     'purple',
	secondary:   'gray',
	destructive: 'red',
	outline:     'gray',
};

function resolveColorClasses(variant: BadgeVariant, color: BadgeColor): string {
	if (variant === 'destructive') return destructiveClass;
	if (variant === 'primary')     return secondaryClasses[color];
	if (variant === 'outline')     return outlineClasses[color];
	return primaryClasses[color];
}

const Dot = () => (
	<span
		className='size-1.5 shrink-0 rounded-full'
		style={{ backgroundColor: 'var(--dot)' }}
	/>
);

export function Badge({
	children,
	variant = 'secondary',
	color,
	startIcon,
	endIcon,
	onRemove,
	className,
}: BadgeProps) {
	const resolvedColor = color ?? variantDefaultColor[variant];
	const colorCls = resolveColorClasses(variant, resolvedColor);

	const hasStart = startIcon !== undefined;
	const hasEnd = endIcon !== undefined;
	const hasRemove = onRemove !== undefined;

	return (
		<span
			className={twMerge(
				'inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium leading-4',
				colorCls,
				className,
			)}>
			{hasStart ? (
				<span className='inline-flex shrink-0 items-center'>{startIcon}</span>
			) : (
				<Dot />
			)}

			{children}

			{hasEnd && (
				<span className='inline-flex shrink-0 items-center'>{endIcon}</span>
			)}

			{hasRemove && (
				<button
					type='button'
					aria-label='Remover'
					onClick={onRemove}
					className='inline-flex shrink-0 items-center rounded-full opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current'>
					<X className='size-3' />
				</button>
			)}
		</span>
	);
}
